import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel, TraitValueModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductImageCarousel, {
    ProductImageCarouselStyles,
} from "@insite/content-library/Components/ProductImageCarousel";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { productCopyCss } from "@SupplyLand/Utilities/richTextStyles";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: HasProduct) => {
    const productData = Object.values(state.data.products.byId);
    return {
        selectedImage: getSelectedImage(state, ownProps.product),
        selectedImageIndex: state.pages.productDetails.selectedImageIndex,
        productData,
    };
};

const mapDispatchToProps = {
    setSelectedImageIndex,
};

export interface ProductDetailsPresentationDataStyles {
    /**
     * @deprecated Use primaryImage styles instead.
     */
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;

    primaryImage?: ProductPrimaryImageStyles;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    imageCarousel?: ProductImageCarouselStyles;
    titleText?: TypographyProps;
    productNumberText?: TypographyProps;
    mobileHidden?: HiddenProps;
    wrapper?: InjectableCss;
    priceCodeWrapper?: any;
    priceCodeLabel?: TypographyPresentationProps;
    priceCodeMessage?: TypographyPresentationProps;
    priceCodeMessageWrapper?: InjectableCss;
}

export const primaryImageStyles: ProductDetailsPresentationDataStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 70vh;
                }
            `,
        },
        img: {
            css: css`
                width: auto;
                max-height: 70vh;
            `,
        },
    },
    titleText: {
        variant: "h4",
        css: css`
            margin-bottom: 2px;
        `,
    },
    productNumberText: {
        variant: "h6",
        css: css`
            margin-bottom: 2px;
        `,
    },
    mobileHidden: {
        above: "sm",
    },
    // L4 description (mobile): same copy rules as the desktop Description widget
    wrapper: {
        css: css`
            margin-top: 10px;
            margin-bottom: -50px;
            ${productCopyCss}
        `,
    },
    priceCodeWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            align-items: center;
            margin-bottom: 20px;
        `,
    },
    priceCodeLabel: {
        css: css`
            color: rgb(230, 78, 37);
            font-weight: 600;
        `,
    },
    priceCodeMessage: {
        css: css`
            color: rgb(230, 78, 37);
        `,
    },
    priceCodeMessageWrapper: {
        css: css`
            ${wrapInContainerStyles}
        `,
    },
    imageCarousel: {
        carouselWrapper: {
            css: css`
                height: 100%;
                cursor: pointer;
            `,
        },
    },
};

const styles = primaryImageStyles;

const ProductDetailsPresentationData: React.FC<Props> = ({
    product,
    selectedImage,
    selectedImageIndex,
    setSelectedImageIndex,
    productData,
}) => {
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = React.useState(false);
    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        setSelectedImageIndex({ index });
    };

    if (!product) {
        return null;
    }

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const path = image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }

    let productNumber = product.productNumber;
    const isParentChildConnector =
        product.variantTraits?.some(variant => variant.nameDisplay === "Parent Child Connector") &&
        product.isVariantParent;
    const connectorAttribute = isParentChildConnector
        ? product.variantTraits?.find(variant => variant.nameDisplay === "Parent Child Connector")
        : null;
    const traitValue =
        connectorAttribute && connectorAttribute.traitValues && connectorAttribute.traitValues.length > 0
            ? connectorAttribute.traitValues[0]
            : undefined;
    if (connectorAttribute && traitValue) {
        const childProduct = productData.find(
            child => child?.childTraitValues?.find(childTrait => childTrait?.id === traitValue.id) !== undefined,
        );
        if (childProduct) {
            productNumber = !childProduct.isVariantParent ? childProduct.productNumber : "";
        }
    }

    const renderProductImageCarousel = () => {
        const selectImageHandler = (index: number) => {
            setSelectedImageIndex({ index });
        };

        if (!product || !product.images || product.images.length <= 1) {
            return null;
        }

        return (
            <ProductImageCarousel
                selectedIndex={selectedImageIndex}
                onSelectImage={selectImageHandler}
                extendedStyles={styles.imageCarousel}
                numberOfVisibleThumbnails={5}
            />
        );
    };

    return (
        <>
            <Hidden {...styles.mobileHidden}>
                <Typography {...styles.titleText}>{product.productTitle}</Typography>
                {!product.isVariantParent && <Typography {...styles.productNumberText}>{productNumber}</Typography>}
                {isParentChildConnector &&
                    product.variantTraits &&
                    product.variantTraits.length === 1 &&
                    connectorAttribute &&
                    connectorAttribute.traitValues &&
                    connectorAttribute.traitValues.length === 1 && (
                        <Typography {...styles.productNumberText}>{productNumber}</Typography>
                    )}
            </Hidden>
            <ProductPrimaryImage
                product={product}
                image={image}
                onClick={imageClickHandler}
                extendedStyles={styles.primaryImage}
            />
            {renderProductImageCarousel()}
            {product.detail?.priceCode && (
                <Hidden {...styles.mobileHidden}>
                    <StyledWrapper {...styles.priceCodeWrapper}>
                        <Typography {...styles.priceCodeLabel}>{translate("Free_Offer")}&nbsp;</Typography>
                        <Typography {...styles.priceCodeMessage}>
                            {translate(`ProductDetail_${product.detail?.priceCode}`)}
                        </Typography>
                    </StyledWrapper>
                </Hidden>
            )}
            {product.content && (
                <Hidden {...styles.mobileHidden}>
                    <StyledWrapper {...styles.wrapper}>
                        <div data-test-selector="productDetails_htmlContent">
                            {parse(product.content.htmlContent, parserOptions)}
                        </div>
                    </StyledWrapper>
                </Hidden>
            )}
            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <ProductPrimaryImage
                    product={product}
                    image={image}
                    useLargeImage={true}
                    extendedStyles={styles.fullPrimaryImage}
                />
                {product.images && product.images.length > 1 && (
                    <ProductImageCarousel
                        selectedIndex={selectedImageIndex}
                        onSelectImage={selectImageHandler}
                        extendedStyles={styles.imageCarousel}
                        numberOfVisibleThumbnails={5}
                    />
                )}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPresentationData)),
    definition: {
        displayName: "Product Presentation Data",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
